import {
  Gender,
  TeeColor,
  useGetCourseSuspenseQuery,
  useGetHoleSuspenseQuery,
} from '@slope/graphql/apollo';
import { createStore, StoreProvider, useStore } from '@slope/store';
import { useNavigate, useParams } from 'react-router';
import { Viewer } from '../components';
import { useEffect, useMemo, useState } from 'react';
import * as THREE from 'three';
import { HoleInfo } from '../components/HoleInfo';
import { Flex } from '@slope/uikit';
import { Leva, useControls } from 'leva';

export const HoleView: React.FC = () => {
  const { clubSlug } = useParams<{
    clubSlug?: string;
  }>();

  const availableHoles = useStore(({ availableHoles }) => availableHoles);
  const selectedHole = useStore(({ selectedHole }) => selectedHole);
  const availableTees = useMemo(
    () => selectedHole?.tees.filter(({ gender }) => gender === Gender.Male) ?? [],
    [selectedHole],
  );

  const navigate = useNavigate();

  useControls(
    'Hole Selector',
    {
      hole: {
        value: selectedHole ? `hole_${selectedHole.number}` : '',
        options: availableHoles.map(({ number }) => `hole_${number}`),
        label: 'Select Hole',
        onChange: (value: string) => {
          value &&
            navigate(
              [`/${clubSlug}/${value.replace('hole_', '')}`, document.location.search]
                .filter(Boolean)
                .join('?'),
            );
        },
      },
    },
    { collapsed: false },
    [availableHoles],
  );

  const availableTeeColors = useMemo(() => {
    const colors = new Set<TeeColor>();
    selectedHole?.tees.forEach(({ color }) => colors.add(color));
    return Array.from(colors).sort();
  }, [selectedHole]);

  const { teeColor } = useControls(
    'Tee Selector',
    {
      teeColor: {
        value: availableTeeColors[0] || TeeColor.Black,
        options: availableTeeColors,
      },
    },
    { collapsed: false },
    [availableTeeColors],
  );

  const selectedTee = useMemo(() => {
    return selectedHole?.tees.find(({ color }) => color === teeColor) ?? null;
  }, [selectedHole, teeColor]);

  return (
    <Flex height="100vh" width="100vw" position="relative" overflow="hidden">
      <Flex position="absolute" top={0} left={0} zIndex={100} gap="1rem" padding="1rem">
        {selectedHole && (
          <HoleInfo
            key={selectedHole.id}
            number={selectedHole.number ?? '?'}
            par={selectedHole.par ?? '?'}
            strokeIndex={selectedHole.strokeIndex ?? '?'}
            distance={selectedTee?.length ?? '?'}
            color={selectedTee?.color ?? TeeColor.Black}
          />
        )}
      </Flex>
      {selectedHole && <Viewer hole={selectedHole} />}
    </Flex>
  );
};
